"use client";

import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Button, FormInput } from "@offstreetparking/design-system/forms";
import { Link, Text } from "@offstreetparking/design-system/typography";
import { Form } from "@offstreetparking/portal-flow/components/forms/Form";
import { PRIVACY_URL, TERMS_URL } from "@offstreetparking/portal-flow/lib/legal-urls";
import { useMutation } from "@tanstack/react-query";
import { useId } from "react";
import * as z from "zod/v4";
import { OrDivider } from "~/components/auth/OrDivider";
import { ProviderLoginButtons } from "~/components/auth/ProviderLoginButtons";
import { PasswordRequirements, createPasswordSchema } from "~/components/forms/PasswordRequirements";
import { useCompany } from "~/components/providers/CompanyProvider";
import type { AuthError } from "~/lib/auth";
import { AUTH_ERROR_MESSAGES } from "~/lib/auth/i18n-error";
import { routes } from "~/routes";
const createSignUpSchema = ({ i18n, passwordMatchMessage }: { i18n: I18n; passwordMatchMessage: string }) =>
	z
		.object({
			firstName: z.string().min(1),
			lastName: z.string().min(1),
			email: z.email(),
			password: createPasswordSchema({ i18n }),
			confirmPassword: z.string(),
			companyId: z.number().optional(),
			subdomain: z.string().optional(),
			inviteToken: z.string().optional(),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: passwordMatchMessage,
			path: ["confirmPassword"],
		});
type SignUpFormData = z.infer<ReturnType<typeof createSignUpSchema>>;
export type SignUpFormProps = {
	onSignUp: (data: SignUpFormData) => Promise<void>;
	redirectTo?: string;
	inviteToken?: string;
	email?: string;
	onSwitchTo?: (target: "signIn") => void;
	onProviderSignedIn?: () => void;
};
export const SignUpFormContent = ({
	onSignUp,
	redirectTo,
	inviteToken,
	email,
	onSwitchTo,
	onProviderSignedIn,
}: SignUpFormProps) => {
	const { t, i18n } = useLingui();
	const formId = useId();
	const { company, isFromSubdomain } = useCompany();
	const {
		mutate: signUp,
		isPending,
		error,
	} = useMutation<void, AuthError, SignUpFormData>({
		mutationFn: onSignUp,
	});
	const isNextRedirectError = error?.message === "NEXT_REDIRECT";
	const errorMessage = error?.i18nKey ? i18n._(AUTH_ERROR_MESSAGES[error.i18nKey]) : error?.message;
	return (
		<>
			{error && !isNextRedirectError && (
				<div className="rounded-md bg-critical-faded p-4">
					{!error.errors && (
						<Text size="sm" className="text-critical">
							{errorMessage}
						</Text>
					)}
					{!!error.errors && error.errors.length > 1 && (
						<Text size="sm" className="text-critical">
							{errorMessage}
							<ul className="mt-2 list-disc space-y-1 pl-5">
								{error.errors.map((err) => (
									<li key={err.code}>{err.message}</li>
								))}
							</ul>
						</Text>
					)}
					{!!error.errors && error.errors.length === 1 && (
						<Text size="sm" className="text-critical">
							{error.errors[0]?.message ?? errorMessage}
						</Text>
					)}
				</div>
			)}
			<Form
				id={formId}
				className="space-y-6"
				schema={createSignUpSchema({ i18n, passwordMatchMessage: t`Passwords must match` })}
				initialValues={{
					firstName: "",
					lastName: "",
					email: email ?? "",
					password: "",
					confirmPassword: "",
					companyId: company?.id,
					subdomain: isFromSubdomain ? company?.subdomain : undefined,
					inviteToken,
				}}
				onSubmit={(values: SignUpFormData) => signUp(values)}
			>
				<FormInput name="firstName" label={t`First Name`} autoComplete="given-name" isRequired />

				<FormInput name="lastName" label={t`Last Name`} autoComplete="family-name" isRequired />

				<FormInput
					name="email"
					type="email"
					label={t`Email`}
					autoComplete="email"
					isRequired
					disabled={!!inviteToken}
				/>

				<FormInput name="password" type="password" label={t`Password`} autoComplete="new-password" isRequired />

				<PasswordRequirements name="password" />

				<FormInput
					name="confirmPassword"
					type="password"
					label={t`Confirm Password`}
					autoComplete="new-password"
					isRequired
				/>

				<Button isLoading={isPending} type="submit" form={formId} className="w-full">
					{t`Create Account`}
				</Button>
				<OrDivider />

				<ProviderLoginButtons redirectTo={redirectTo} onSignedIn={onProviderSignedIn} />

				<Text size="sm">
					<Trans>
						By creating an account, I accept Offstreet’s{" "}
						<Link href={TERMS_URL} isExternal>
							Terms of Service
						</Link>{" "}
						and{" "}
						<Link href={PRIVACY_URL} isExternal>
							Privacy Policy
						</Link>
					</Trans>
				</Text>

				<hr className="border-neutral-faded" />

				<div className="space-y-2 pt-2 text-center">
					<Text weight="medium">{t`Already have an account?`}</Text>
					{onSwitchTo ? (
						<Button
							intent="secondary"
							type="button"
							onClick={() => onSwitchTo("signIn")}
							className="w-full"
						>
							{t`Sign In`}
						</Button>
					) : (
						<Button
							intent="secondary"
							href={routes.login({
								redirectTo,
							})}
							className="w-full"
						>
							{t`Sign In`}
						</Button>
					)}
				</div>
			</Form>
		</>
	);
};
